import React from 'react';
import { Loader2, ChevronDown, Folder, Lock, Link2, Globe } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CATEGORY_ICONS, CATEGORY_COLORS, ICON_OPTIONS } from './constants';
import { getCategoryIcon } from './utils';

// ─── CategoryModal ─────────────────────────────────────────────────
// Create-or-edit modal for a Collection. All form state lives in the
// parent (Dashboard.js) so multiple entry points can pre-fill it (the
// sidebar `+` button, the per-category `Add sub-collection` menu, and
// the per-category `Edit` menu). This component is presentational —
// it renders the form and forwards every change back via setters.
//
// Sections, in order:
//   • Name (required)
//   • Description (optional, 140-char cap for share-preview snippets)
//   • Privacy radio row (Private / Unlisted / Public)
//   • Icon picker (disclosure → grid of ICON_OPTIONS)
//   • Color picker (disclosure → grid of CATEGORY_COLORS)
//   • Parent collection Select
//   • Manual-order + numbered-ranking toggles ("Top N" collections)
export function CategoryModal({
    open,
    onOpenChange,
    editingCategory,
    // form fields
    categoryName, setCategoryName,
    categoryDescription, setCategoryDescription,
    categoryShareVisibility, setCategoryShareVisibility,
    categoryIcon, setCategoryIcon,
    categoryColor, setCategoryColor,
    categoryParentId, setCategoryParentId,
    categoryManualOrder, setCategoryManualOrder,
    categoryShowNumbering, setCategoryShowNumbering,
    // disclosure state
    iconPickerOpen, setIconPickerOpen,
    colorPickerOpen, setColorPickerOpen,
    // data + actions
    categories,
    handleSaveCategory,
    submitting,
    resetCategoryForm,
}) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="bg-white rounded-2xl sm:rounded-3xl shadow-2xl p-0 max-w-[calc(100vw-2rem)] sm:max-w-lg max-h-[90dvh] flex flex-col overflow-hidden">
                <DialogHeader className="px-4 sm:px-6 pt-4 sm:pt-6 pb-3 border-b border-zinc-100 flex-shrink-0">
                    <DialogTitle className="font-heading text-xl">
                        {editingCategory ? 'Edit Collection' : 'New Collection'}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSaveCategory} className="flex flex-col flex-1 min-h-0">
                    {/* Scrollable body — sticky footer stays visible on
                        short viewports even when the form is long. */}
                    <div className="flex-1 min-h-0 overflow-y-auto px-4 sm:px-6 py-4 space-y-4" data-testid="category-modal-scroll">
                        <div>
                            <label className="text-sm font-medium mb-1.5 block">Name</label>
                            <Input
                                placeholder="e.g., AI Research, Recipes..."
                                value={categoryName}
                                onChange={(e) => setCategoryName(e.target.value)}
                                className="rounded-xl"
                                autoFocus
                                data-testid="category-name-input"
                            />
                        </div>
                        <div>
                            <div className="flex items-baseline justify-between mb-1.5">
                                <label className="text-sm font-medium">
                                    Description <span className="text-muted-foreground font-normal">(optional)</span>
                                </label>
                                <span className="text-[11px] text-muted-foreground tabular-nums">{categoryDescription.length}/140</span>
                            </div>
                            <Input
                                placeholder='e.g., "My top 10 Seinfeld moments"'
                                value={categoryDescription}
                                onChange={(e) => setCategoryDescription(e.target.value.slice(0, 140))}
                                maxLength={140}
                                className="rounded-xl"
                                data-testid="category-description-input"
                            />
                            <p className="text-[11px] text-muted-foreground mt-1">
                                Shown under the title - and in WhatsApp/Telegram previews when you share the collection.
                            </p>
                        </div>

                        {/* Privacy radio row */}
                        <div>
                            <label className="text-sm font-medium mb-1.5 block">Privacy</label>
                            <div className="grid grid-cols-3 gap-2" role="radiogroup" aria-label="Collection privacy">
                                {[
                                    { value: 'private',  label: 'Private',  icon: Lock,  desc: 'Only you',           color: 'zinc' },
                                    { value: 'unlisted', label: 'Unlisted', icon: Link2, desc: 'Anyone with link',   color: 'sky' },
                                    { value: 'public',   label: 'Public',   icon: Globe, desc: 'On your profile',    color: 'emerald' },
                                ].map((opt) => {
                                    const Icon = opt.icon;
                                    const active = categoryShareVisibility === opt.value;
                                    const ring = active
                                        ? (opt.color === 'emerald'
                                            ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                            : opt.color === 'sky'
                                                ? 'border-sky-500 bg-sky-50 text-sky-700'
                                                : 'border-zinc-400 bg-zinc-50 text-zinc-700')
                                        : 'border-zinc-200 hover:border-zinc-300 text-zinc-700';
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            role="radio"
                                            aria-checked={active}
                                            onClick={() => setCategoryShareVisibility(opt.value)}
                                            className={`text-left rounded-xl border px-2.5 py-2 transition-all ${ring}`}
                                            data-testid={`privacy-${opt.value}-radio`}
                                        >
                                            <div className="flex items-center gap-1.5 text-[13px] font-semibold">
                                                <Icon className="w-3.5 h-3.5 shrink-0" />
                                                {opt.label}
                                            </div>
                                            <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{opt.desc}</div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Icon picker */}
                        <div>
                            {(() => {
                                const SelectedIcon = CATEGORY_ICONS[categoryIcon] || Folder;
                                const selectedName = (ICON_OPTIONS.find((o) => o.value === categoryIcon) || {}).name || 'Folder';
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setIconPickerOpen((v) => !v)}
                                        className="w-full flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
                                        data-testid="icon-picker-disclosure"
                                        aria-expanded={iconPickerOpen}
                                    >
                                        <span
                                            className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                                            style={{ backgroundColor: categoryColor + '1A' }}
                                        >
                                            <SelectedIcon className="w-5 h-5" style={{ color: categoryColor }} />
                                        </span>
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-medium text-foreground">Icon</span>
                                            <span className="block text-[12px] text-muted-foreground truncate">
                                                {iconPickerOpen ? 'Tap to collapse' : `${selectedName} · tap to change`}
                                            </span>
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${iconPickerOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                );
                            })()}
                            {iconPickerOpen && (
                                <div className="flex flex-wrap gap-2 mt-3" data-testid="icon-picker-grid">
                                    {ICON_OPTIONS.map((icon) => {
                                        const IconComponent = CATEGORY_ICONS[icon.value] || Folder;
                                        return (
                                            <button
                                                key={icon.value}
                                                type="button"
                                                onClick={() => setCategoryIcon(icon.value)}
                                                className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${
                                                    categoryIcon === icon.value ? 'ring-2 ring-primary bg-accent' : 'bg-secondary hover:bg-secondary'
                                                }`}
                                                aria-label={icon.name}
                                                title={icon.name}
                                            >
                                                <IconComponent className="w-5 h-5" style={{ color: categoryColor }} />
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Color picker */}
                        <div>
                            {(() => {
                                const selectedColorName = (CATEGORY_COLORS.find((c) => c.value === categoryColor) || {}).name || 'Custom';
                                return (
                                    <button
                                        type="button"
                                        onClick={() => setColorPickerOpen((v) => !v)}
                                        className="w-full flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-3 py-2.5 text-left hover:bg-zinc-50 transition-colors"
                                        data-testid="color-picker-disclosure"
                                        aria-expanded={colorPickerOpen}
                                    >
                                        <span
                                            className="w-9 h-9 rounded-lg shrink-0 ring-1 ring-black/5"
                                            style={{ backgroundColor: categoryColor }}
                                        />
                                        <span className="flex-1 min-w-0">
                                            <span className="block text-sm font-medium text-foreground">Color</span>
                                            <span className="block text-[12px] text-muted-foreground truncate">
                                                {colorPickerOpen ? 'Tap to collapse' : `${selectedColorName} · tap to change`}
                                            </span>
                                        </span>
                                        <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${colorPickerOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                );
                            })()}
                            {colorPickerOpen && (
                                <div className="flex flex-wrap gap-2 mt-3" data-testid="color-picker-grid">
                                    {CATEGORY_COLORS.map((color) => (
                                        <button
                                            key={color.value}
                                            type="button"
                                            onClick={() => setCategoryColor(color.value)}
                                            className={`w-10 h-10 rounded-xl transition-all ${
                                                categoryColor === color.value ? 'ring-2 ring-offset-2 ring-foreground scale-110' : 'hover:scale-105'
                                            }`}
                                            aria-label={color.name}
                                            title={color.name}
                                            style={{ backgroundColor: color.value }}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Parent collection Select */}
                        <div>
                            <label className="text-sm font-medium mb-1.5 block">Parent collection (optional)</label>
                            <Select value={categoryParentId || 'none'} onValueChange={(val) => setCategoryParentId(val === 'none' ? '' : val)}>
                                <SelectTrigger className="rounded-xl" data-testid="category-parent-select">
                                    <SelectValue placeholder="None (top-level)" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (top-level)</SelectItem>
                                    {categories
                                        .filter((c) => !c.parent_id && c.category_id !== editingCategory?.category_id)
                                        .map((cat) => {
                                            const IconComponent = getCategoryIcon(cat.icon);
                                            return (
                                                <SelectItem key={cat.category_id} value={cat.category_id}>
                                                    <div className="flex items-center gap-2">
                                                        <IconComponent className="w-4 h-4" style={{ color: cat.color }} />
                                                        {cat.name}
                                                    </div>
                                                </SelectItem>
                                            );
                                        })}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Manual ordering + numbered ranking toggles */}
                        <div className="rounded-xl border border-zinc-200 bg-zinc-50/60 p-3 space-y-3">
                            <label className="flex items-start gap-3 cursor-pointer" data-testid="manual-order-toggle">
                                <input
                                    type="checkbox"
                                    checked={categoryManualOrder}
                                    onChange={(e) => {
                                        const next = e.target.checked;
                                        setCategoryManualOrder(next);
                                        if (!next) setCategoryShowNumbering(false);
                                    }}
                                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Manual order</div>
                                    <div className="text-[12px] text-muted-foreground">Drag-and-drop the cards in this collection to set their order.</div>
                                </div>
                            </label>
                            <label className="flex items-start gap-3 cursor-pointer" data-testid="show-numbering-toggle">
                                <input
                                    type="checkbox"
                                    checked={categoryShowNumbering}
                                    onChange={(e) => {
                                        const next = e.target.checked;
                                        setCategoryShowNumbering(next);
                                        if (next) setCategoryManualOrder(true);
                                    }}
                                    className="mt-0.5 h-4 w-4 rounded border-zinc-300 text-primary focus:ring-primary"
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium">Show numbered ranking</div>
                                    <div className="text-[12px] text-muted-foreground">Adds #1, #2, #3… badges on each card. Great for Top 10 lists.</div>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Sticky action footer */}
                    <div className="flex gap-2 px-4 sm:px-6 py-3 border-t border-zinc-100 bg-white flex-shrink-0">
                        <Button
                            type="button"
                            variant="outline"
                            className="flex-1 rounded-xl"
                            onClick={() => { onOpenChange(false); resetCategoryForm(); }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="flex-1 rounded-xl bg-primary"
                            disabled={submitting || !categoryName.trim()}
                            data-testid="submit-category-btn"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {editingCategory ? 'Update' : 'Create'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
